import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getAdminUtilitiesApi, getUtilityRateAnalysisApi } from '../../apis/apis'
import type { TariffRateAnalysisRow, Utility } from '../../apis/types'
import Layout from '../programs/Layout'
import QueryError from '../conductor/QueryError'
import { ComboboxAutocomplete } from '../../components/Form/ComboboxAutocomplete'
import { formatUtilityTypeLabel } from './components/TariffChips'
import { TariffPageHeader } from './components/TariffPageHeader'
import { TariffRateValidationTable } from './components/TariffRateValidationTable'

const UTILITY_TYPE_ORDER: Record<string, number> = {
    IOU: 0,
    CCA: 1,
    ESP_DA: 2,
}

const TariffRateValidation: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const utilityCuid = searchParams.get('utilityCuid') ?? ''
    const schedule = searchParams.get('schedule') ?? ''
    const [utilities, setUtilities] = useState<Utility[]>([])
    const [allRows, setAllRows] = useState<TariffRateAnalysisRow[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    const [reloadToken, setReloadToken] = useState(0)

    useEffect(() => {
        void Promise.all([
            getAdminUtilitiesApi('IOU'),
            getAdminUtilitiesApi('CCA'),
            getAdminUtilitiesApi('ESP_DA'),
        ]).then(([iou, cca, da]) => setUtilities([...iou, ...cca, ...da]))
    }, [])

    useEffect(() => {
        if (utilityCuid === '') {
            setAllRows([])
            return
        }
        setLoading(true)
        setError(false)
        getUtilityRateAnalysisApi(utilityCuid)
            .then(setAllRows)
            .catch(() => setError(true))
            .finally(() => setLoading(false))
    }, [utilityCuid, reloadToken])

    const utilityOptions = useMemo(
        () =>
            [...utilities]
                .map((utility) => ({
                    id: utility.id,
                    name:
                        utility.shortName != null && utility.shortName !== ''
                            ? `${utility.shortName} — ${utility.name}`
                            : utility.name,
                    description: formatUtilityTypeLabel(utility.type),
                    type: utility.type ?? '',
                }))
                .sort((a, b) => {
                    const typeDiff =
                        (UTILITY_TYPE_ORDER[a.type] ?? 99) - (UTILITY_TYPE_ORDER[b.type] ?? 99)
                    if (typeDiff !== 0) return typeDiff
                    return a.name.localeCompare(b.name)
                }),
        [utilities]
    )

    useEffect(() => {
        if (utilityCuid !== '' || utilityOptions.length === 0) return
        const first = utilityOptions[0]
        if (first == null) return
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev)
                next.set('utilityCuid', String(first.id))
                return next
            },
            { replace: true }
        )
    }, [utilityCuid, utilityOptions, setSearchParams])

    const scheduleOptions = useMemo(() => {
        const unique = new Set(allRows.map((row) => row.schedule))
        if (schedule !== '') unique.add(schedule)
        return [...unique]
            .sort((a, b) => a.localeCompare(b))
            .map((value) => ({ id: value, name: value }))
    }, [allRows, schedule])

    const rows = useMemo(() => {
        if (schedule === '') return allRows
        return allRows.filter((row) => row.schedule.toLowerCase() === schedule.toLowerCase())
    }, [allRows, schedule])

    const selectedUtility = utilities.find((utility) => utility.id === utilityCuid)
    const handleExport = (): void => {
        if (rows.length === 0) return
        const headers = [
            'schedule',
            'rateEffectiveDate',
            'season',
            'touPeriod',
            'vintageYear',
            'totalRate',
            'generationRate',
            'deliveryRate',
            'nbcRate',
            'tariffStatus',
            'ratesetStatus',
        ]
        const csv = [
            headers.join(','),
            ...rows.map((row) =>
                [
                    row.schedule,
                    row.rateEffectiveDate,
                    row.season ?? '',
                    row.touPeriod ?? '',
                    row.vintageYear ?? '',
                    row.totalRate,
                    row.generationRate,
                    row.deliveryRate,
                    row.nbcRate,
                    row.tariffStatus ?? '',
                    row.ratesetStatus ?? '',
                ]
                    .map((value) => `"${String(value).replaceAll('"', '""')}"`)
                    .join(',')
            ),
        ].join('\n')
        const blob = new Blob([csv], { type: 'text/csv' })
        const url = URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = url
        const shortName = selectedUtility?.shortName ?? selectedUtility?.name ?? 'utility'
        anchor.download = `rate-validation-${shortName}.csv`
        anchor.click()
        URL.revokeObjectURL(url)
    }

    return (
        <Layout
            title={
                <TariffPageHeader
                    showSectionTabs
                    title="Rate validation"
                    subtitle="Generation, delivery, and NBC rollups by rateset."
                />
            }
        >
            <div className="mb-4 flex flex-wrap items-end gap-3">
                <div className="min-w-[28rem]">
                    <ComboboxAutocomplete
                        label="Utility"
                        labelPlural="Utilities"
                        value={utilityCuid === '' ? null : utilityCuid}
                        handleChange={(id) => {
                            setSearchParams((prev) => {
                                const next = new URLSearchParams(prev)
                                if (id == null) return prev
                                next.set('utilityCuid', String(id))
                                next.delete('schedule')
                                return next
                            })
                        }}
                        data={utilityOptions}
                        wildCardDisabled
                        isNotFilter
                        placeholder="Search utilities..."
                    />
                </div>
                <div className="min-w-[20rem]">
                    <ComboboxAutocomplete
                        label="Schedule"
                        labelPlural="Schedules"
                        value={schedule === '' ? null : schedule}
                        handleChange={(id) => {
                            setSearchParams((prev) => {
                                const next = new URLSearchParams(prev)
                                if (id == null) next.delete('schedule')
                                else next.set('schedule', String(id))
                                return next
                            })
                        }}
                        data={scheduleOptions}
                        disabled={utilityCuid === ''}
                        placeholder="Search schedules..."
                    />
                </div>
                <button
                    type="button"
                    className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                    onClick={handleExport}
                    disabled={rows.length === 0}
                >
                    Download CSV
                </button>
            </div>
            {error ? (
                <QueryError
                    message="Failed to load rate validation."
                    onRetry={() => {
                        setError(false)
                        setReloadToken((token) => token + 1)
                    }}
                />
            ) : (
                <TariffRateValidationTable rows={rows} showSchedule loading={loading} />
            )}
        </Layout>
    )
}

export default TariffRateValidation
